"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormModelFieldSubForm = exports.FormModelMutatorSubForm = void 0;
const core_api_1 = require("@ikegps/core-api");
const id_1 = require("../../id");
const util_1 = require("../../math/util");
const collection_1 = require("../collection");
const form_field_1 = require("./form.field");
const form_select_list_1 = require("./form.select.list");
const form_subform_span_1 = require("./form.subform.span");
const form_nested_list_1 = require("./nested.list/form.nested.list");
const TITLE_FIELD_TYPES = [
    core_api_1.IkeFieldType.TEXT,
    core_api_1.IkeFieldType.NUMBER,
    core_api_1.IkeFieldType.SELECT_LIST,
    core_api_1.IkeFieldType.NESTED_LIST,
];
const SITE_TYPE_FIELD = 'Site Type';
const DROP_POLE_FIELD = 'Drop Pole';
const POLE_LOADING_FIELD = 'Loading Percentage';
const SPAN_COLORS = [
    '#03a8a0',
    '#f16623',
    '#21409a',
    '#ff0984',
    '#66d313',
    '#f44546',
    '#039c4b',
    '#04adff',
    '#e48873',
    '#fedf17',
];
class FormModelMutatorSubForm extends form_field_1.FormModelMutatorField {
    getFieldIds() {
        const stateValue = this.field.stateValue;
        if (Array.isArray(stateValue.fields)) {
            return stateValue.fields.slice();
        }
        return [];
    }
    create(type, fieldId, index) {
        if (fieldId == null || fieldId === '') {
            fieldId = id_1.IkeId.generate(10);
        }
        const stateId = this.field.id + ':' + fieldId;
        const fields = this.getFieldIds();
        if (fields.indexOf(fieldId) > -1 || this.state.getFieldValue(stateId) != null) {
            throw new Error(`FieldId must be unique: ${fieldId}`);
        }
        if (index == null || index < 0) {
            fields.push(fieldId);
        }
        else {
            fields.splice(index + 1, 0, fieldId);
        }
        this.state.setFieldValue(stateId, { type, id: fieldId, displayName: '' });
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { fields }));
        return this.field.getField(fieldId);
    }
    remove(fieldId) {
        if (!this.field.stateValue) {
            // This SubForm field has been deleted so there is nothing to delete.
            return;
        }
        const fields = this.getFieldIds();
        const index = fields.indexOf(fieldId);
        if (index === -1) {
            throw new Error(`Cannot remove field. "${fieldId}" is missing from form "${this.field.id}"`);
        }
        fields.splice(index, 1);
        // Clear metadata references if this field is referenced.
        const form = this.field.getForm();
        if (form.metadata.statusFieldId === fieldId) {
            form.metadata.setStatusField(null);
        }
        if (form.metadata.mapColorFieldId === fieldId) {
            form.metadata.setMapColorField(null);
        }
        if (form.metadata.addressLookupFieldId === fieldId) {
            form.metadata.setAddressLookupField(null);
        }
        // Remove the field from this SubForm's list of owned fields.
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { fields }));
        // Remove the field's state.
        const stateId = this.field.id + ':' + fieldId;
        this.state.removeField(stateId);
        // Remove the field from this SubForm's field cache.
        delete this.field['_fields'][fieldId];
    }
    /**
     * Move a field in a direction
     *
     * @param fieldId Id of field to move
     * @param direction direction to move +1 Down, -1 Up
     * @returns Index of field after move
     */
    move(fieldId, direction) {
        const fields = this.getFieldIds();
        const index = fields.indexOf(fieldId);
        if (index === -1) {
            throw new Error(`Cannot move field. "${fieldId}" is missing from form "${this.field.id}"`);
        }
        const nextPosition = (0, util_1.clamp)(index + direction, 0, fields.length - 1);
        fields.splice(index, 1);
        fields.splice(nextPosition, 0, fieldId);
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { fields }));
        return nextPosition;
    }
    label(label) {
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { label }));
    }
    /**
     * Set a subform admin only state
     *
     * This functions overrides the standard adminOnly function in order to set all children adminOnly if a subform adminOnly parameter is updated
     * @param adminOnly adminOnly parameter value
     */
    adminOnly(adminOnly) {
        super.adminOnly(adminOnly);
        for (let field of this.field.fields) {
            field.Mutator.adminOnly(adminOnly);
        }
    }
    /**
     * Set a subform collector state
     *
     * This functions overrides the standard collector function in order to set all children collector if a subform collector parameter is updated
     * @param adminOnly adminOnly parameter value
     */
    collector(collector) {
        super.collector(collector);
        for (let field of this.field.fields) {
            field.Mutator.collector(collector);
        }
    }
    /**
     * Set the SubForm Type of this field to SPAN.
     *
     * @param vectorField The Span Vector field to use.
     * @param spanTypeField The Span Type field to use.
     * @throws If either of the given fields do not exist.
     */
    spanTypeWithFields(vectorFieldId, spanTypeFieldId) {
        if (!this.field.hasField(vectorFieldId)) {
            throw new Error(`Vector field '${vectorFieldId}' not found`);
        }
        if (!this.field.hasField(spanTypeFieldId)) {
            throw new Error(`Span Type field '${spanTypeFieldId}' not found`);
        }
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { subFormType: core_api_1.SubFormType.SPAN, spanTypeState: {
                vectorFieldId: vectorFieldId,
                typeFieldId: spanTypeFieldId,
            } }));
    }
    /**
     * Set the SubForm Type of this field to SPAN while linking the existing vector and span type fields.
     * @throws If suitable vector or span type fields do not exist (such as for any non-PLA form).
     */
    spanType() {
        const existingVectorField = this.field.fields.find(form_subform_span_1.isValidSpanVectorField);
        const existingTypeField = this.field.fields.find(form_subform_span_1.isValidSpanTypeField);
        if (!existingVectorField) {
            throw new Error('expected span vector field but did not find one');
        }
        if (!existingTypeField) {
            throw new Error('expected span type field but did not find one');
        }
        this.spanTypeWithFields(existingVectorField.id, existingTypeField.id);
    }
    /**
     * Clear the SubForm type.
     */
    clearType() {
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { subFormType: null, spanTypeState: undefined }));
    }
    poleType() {
        this.state.setFieldValue(this.field.fieldId, Object.assign(Object.assign({}, this.field.stateValue), { subFormType: core_api_1.SubFormType.POLE }));
    }
}
exports.FormModelMutatorSubForm = FormModelMutatorSubForm;
class FormModelFieldSubForm extends form_field_1.FormModelField {
    constructor() {
        super(...arguments);
        this._SFMutator = null;
        this._fields = {};
    }
    static isSubForm(field) {
        return field.type === core_api_1.IkeFieldType.FORM;
    }
    get Mutator() {
        if (this._SFMutator == null) {
            this._SFMutator = new FormModelMutatorSubForm(this);
        }
        return this._SFMutator;
    }
    get label() {
        return this.stateValue.label || [];
    }
    get fields() {
        const stateValue = this.stateValue;
        if (stateValue.fields == null) {
            return [];
        }
        return stateValue.fields.map((fieldId) => this.getField(fieldId));
    }
    get getFieldsRecursive() {
        const stateValue = this.stateValue;
        if (stateValue.fields == null) {
            return [];
        }
        return stateValue.fields.flatMap((fieldId) => {
            const field = this.getField(fieldId);
            if (FormModelFieldSubForm.isSubForm(field)) {
                return [field, ...field.getFieldsRecursive];
            }
            else {
                return field;
            }
        });
    }
    /**
     * @argument fieldId The direct ID of the field (without ":" in it).
     */
    getField(fieldId) {
        let existing = this._fields[fieldId];
        if (existing != null) {
            return existing;
        }
        if (this.stateValue == null || this.stateValue.fields == null) {
            throw new Error(`Cannot get field "${fieldId}" because parent field "${this.fieldId}" has no state (it may have been deleted)`);
        }
        const fieldIndex = this.stateValue.fields.indexOf(fieldId);
        if (fieldIndex === -1) {
            throw new Error(`Cannot get field "${fieldId}" because parent field "${this.fieldId}" does not have that fieldId in its list of owned fields`);
        }
        let fieldState = this.getForm().state.getFieldValue(this.id + ':' + fieldId);
        if (fieldState == null) {
            throw new Error('Unable to find field: ' + this.id + ':' + fieldId);
        }
        if (fieldState.type === core_api_1.IkeFieldType.FORM) {
            existing = new FormModelFieldSubForm(this, fieldState.id);
        }
        else if (fieldState.type === core_api_1.IkeFieldType.SELECT_LIST) {
            existing = new form_select_list_1.FormModelFieldSelectList(this, fieldState.id);
        }
        else if (fieldState.type === core_api_1.IkeFieldType.NESTED_LIST) {
            existing = new form_nested_list_1.FormModelFieldNestedList(this, fieldState.id);
        }
        else {
            existing = new form_field_1.FormModelField(this, fieldState.id);
        }
        this._fields[fieldId] = existing;
        return existing;
    }
    /**
     * Returns the field with the given ID, or null if the field does not exist.
     * @argument fieldId The direct ID of the field (without ":" in it).
     */
    getOptionalField(fieldId) {
        const existing = this._fields[fieldId];
        if (existing != null) {
            return existing;
        }
        if (this.stateValue == null || this.stateValue.fields == null) {
            return null;
        }
        const fieldIndex = this.stateValue.fields.indexOf(fieldId);
        if (fieldIndex === -1) {
            return null;
        }
        const fieldState = this.getForm().state.getFieldValue(this.id + ':' + fieldId);
        if (fieldState == null) {
            return null;
        }
        let field;
        if (fieldState.type === core_api_1.IkeFieldType.FORM) {
            field = new FormModelFieldSubForm(this, fieldState.id);
        }
        else if (fieldState.type === core_api_1.IkeFieldType.SELECT_LIST) {
            field = new form_select_list_1.FormModelFieldSelectList(this, fieldState.id);
        }
        else if (fieldState.type === core_api_1.IkeFieldType.NESTED_LIST) {
            field = new form_nested_list_1.FormModelFieldNestedList(this, fieldState.id);
        }
        else {
            field = new form_field_1.FormModelField(this, fieldState.id);
        }
        this._fields[fieldId] = field;
        return field;
    }
    get titleFields() {
        if (this.label == null || this.label.length === 0) {
            return this.titleField ? [this.titleField] : [];
        }
        return this.label.map((f) => this.getField(f));
    }
    get titleField() {
        const fields = this.fields;
        const assignedTitleFields = fields.filter((field) => field.title);
        if (assignedTitleFields.length > 0) {
            return assignedTitleFields[0];
        }
        const candidateTitleFields = fields.filter((field) => TITLE_FIELD_TYPES.indexOf(field.type) !== -1);
        return candidateTitleFields[0];
    }
    get subtitleField() {
        const fields = this.fields;
        const assignedTitleFields = fields.filter((field) => field.subtitle);
        if (assignedTitleFields.length > 0) {
            return assignedTitleFields[0];
        }
        return null;
    }
    get siteTypeField() {
        const fields = this.fields;
        const siteTypeField = fields.filter((field) => field.displayName === SITE_TYPE_FIELD);
        if (siteTypeField.length > 0) {
            return siteTypeField[0];
        }
        return null;
    }
    get dropPoleField() {
        const fields = this.fields;
        const dropPoleField = fields.filter((field) => field.displayName === DROP_POLE_FIELD);
        if (dropPoleField.length > 0) {
            return dropPoleField[0];
        }
        return null;
    }
    get poleLoadingField() {
        const fields = this.fields;
        const poleLoadingField = fields.filter((field) => field.displayName === POLE_LOADING_FIELD);
        if (poleLoadingField.length > 0) {
            return poleLoadingField[0];
        }
        return null;
    }
    hasField(fieldId) {
        var _a, _b;
        // Use nullish access on stateValue in case this field has been deleted.
        return (_b = (_a = this.stateValue) === null || _a === void 0 ? void 0 : _a.fields.includes(fieldId)) !== null && _b !== void 0 ? _b : false;
    }
    /** Get the index of the provided field */
    getFieldIndex(fieldId) {
        return this.stateValue.fields.indexOf(fieldId);
    }
    get subFormType() {
        var _a;
        return (_a = this.stateValue.subFormType) !== null && _a !== void 0 ? _a : null;
    }
    get subFormSpanTypeState() {
        var _a;
        return (_a = this.stateValue.spanTypeState) !== null && _a !== void 0 ? _a : null;
    }
    /**
     * Search for a field in all children sub forms
     */
    findField(fieldId) {
        const fields = this.fields;
        for (const field of fields) {
            if (field.id === fieldId) {
                return field;
            }
            if (FormModelFieldSubForm.isSubForm(field)) {
                const output = field.findField(fieldId);
                if (output != null) {
                    return output;
                }
            }
        }
        return null;
    }
    createField(type, currentOptions = {}, index) {
        const field = this.Mutator.create(type, currentOptions.id, index);
        if (currentOptions.displayName != null) {
            field.Mutator.displayName(currentOptions.displayName);
        }
        if (currentOptions.title) {
            field.Mutator.title(currentOptions.title);
        }
        if (currentOptions.subtitle) {
            field.Mutator.subtitle(currentOptions.subtitle);
        }
        if (currentOptions.adminOnly) {
            field.Mutator.adminOnly(currentOptions.adminOnly);
        }
        if (currentOptions.required) {
            field.Mutator.required(currentOptions.required);
        }
        if (currentOptions.annotationLabelFieldId) {
            field.Mutator.annotationLabelFieldId(currentOptions.annotationLabelFieldId);
        }
        if (currentOptions.clearanceThresholdInMeters) {
            field.Mutator.clearanceThresholdInMeters(currentOptions.clearanceThresholdInMeters);
        }
        if (currentOptions.cloneable != null && currentOptions.cloneable !== core_api_1.IkeCloneable.NONE) {
            field.Mutator.cloneable(currentOptions.cloneable);
        }
        field.Mutator.providers(currentOptions.providers || []);
        if (currentOptions.collector != null) {
            field.Mutator.collector(currentOptions.collector);
        }
        if (currentOptions.locked !== undefined) {
            field.Mutator.setLockState(currentOptions.locked);
        }
        return field;
    }
    /**
     * Returns if the subform instance is of span type
     * @param subform subform instance
     */
    static isSpanType(subform) {
        if (subform.schema.subFormType === core_api_1.SubFormType.SPAN) {
            return true;
        }
        return ['span', 'spans'].includes(subform.schema.displayName.toLowerCase());
    }
    /**
     * Iterates recursively to parent subforms until it finds the root id form and
     * returns the color of the form if it's span
     * @param subform subform to get root form id
     */
    static getSpanColor(subform) {
        var _a, _b;
        if (subform instanceof collection_1.CollectionModelFieldSubForm) {
            if (subform.parent.schema.isRoot) {
                const index = subform.parent.parent.value.indexOf(subform.parent) % 10; // get last digit
                return this.isSpanType(subform.parent) ? ((_a = SPAN_COLORS[index]) !== null && _a !== void 0 ? _a : null) : null;
            }
            return this.getSpanColor(subform.parent.parent);
        }
        if (subform.parent) {
            if (subform.schema.isRoot) {
                const index = subform.parent.value.indexOf(subform) % 10; // get last digit
                return this.isSpanType(subform) ? ((_b = SPAN_COLORS[index]) !== null && _b !== void 0 ? _b : null) : null;
            }
            return this.getSpanColor(subform.parent);
        }
        return null;
    }
}
exports.FormModelFieldSubForm = FormModelFieldSubForm;
//# sourceMappingURL=form.subform.js.map